import React, { memo, useCallback, useMemo, useState } from 'react';
import { YStack, XStack, useTheme } from 'tamagui';
import { useSelector } from 'react-redux';
import { formatRelativeTime } from '../../../utils/dateTime';

import {
  CobuText,
  CobuCard,
  CobuSimpleAvatar,
  CobuView,
  CobuAlert,
  CobuStack
} from '@cobu/ui';
import { ReportUserModal } from '../../ui/ReportUserModal';
import { useDeleteCommentMutation } from '@cobu/api-client';
// import { ReportUserModal } from '@/components/ui/ReportUserModal';
// import { useBlockUserMutation } from '@/services/user/userHooks';
import {
  GroupComment,
  GroupMember
} from '@cobu/types';
// import { Gathering } from '@/types/event';
import { Strings, URLS } from '../../../constants';
import { RootState } from '../../../store';
// import EnhancedReactionSelector from './EnhancedReactionSelector';
import ReactionSummary from './ReactionSummary';
import CommentActions from './CommentActions';
import CommentReplies from './CommentReplies';
import EnhancedReactionSelector from './EnhancedRectionSelector';
import ShowMoreText from './ShowMoreText';
import CommentImageLayout from './CommentImageLayout';
import ReplyIcon from '../../../assets/icons/Reply';
import { useBlockUserMutation } from '../../../hooks/userHooks';
import { logger } from '../../../utils/logger';
import { PlaceEmbedCard } from '../../places/PlaceEmbedCard';

interface CommentItemProps {
  comment: GroupComment;
  isPinned?: boolean;
  currentUserId?: string;
  groupMembers: GroupMember[];
  replyLoading?: boolean;

  // Deep linking
  isHighlighted?: boolean;
  highlightReplyId?: string;
  forceExpandReplies?: boolean;

  // Feature flags
  allowReactions?: boolean;
  allowReplies?: boolean;
  allowEditing?: boolean;
  allowReporting?: boolean;

  // Callbacks
  onReply?: (comment: GroupComment) => void;
  onReact?: (commentId: string, reaction: string | null) => void;
  onEdit?: (comment: GroupComment) => void;
  onDelete?: (comment: GroupComment) => void;
  onReport?: (userId: string) => void;
  onUserProfile?: (userId: string) => void;
  onHidePinned?: (commentId: string) => void;
  onShowReactionModal?: (commentId: string) => void;
  onFlagComment?: (commentId: string) => void;
}

const CommentItem: React.FC<CommentItemProps> = memo(({
  comment,
  isPinned = false,
  currentUserId: propCurrentUserId,
  groupMembers,
  replyLoading = false,
  isHighlighted = false,
  highlightReplyId,
  forceExpandReplies = false,
  allowReactions = true,
  allowReplies = true,
  allowEditing = true,
  allowReporting = true,
  onReply,
  onReact,
  onEdit,
  onDelete: propOnDelete,
  onReport,
  onUserProfile,
  onHidePinned,
  onShowReactionModal,
  onFlagComment,
}) => {
  const theme = useTheme();
  const strings = Strings.get().discussion;
  const { user } = useSelector((state: RootState) => state.auth);

  // State for report modal
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [reportedUserId, setReportedUserId] = useState<string>(comment.user.uuid);
  const [reportSuccessAlertOpen, setReportSuccessAlertOpen] = useState(false);

  // Mutations and hooks
  const deleteCommentMutation = useDeleteCommentMutation();
  const blockUserMutation = useBlockUserMutation();

  // Use prop currentUserId if provided, otherwise get from auth state
  const currentUserId = propCurrentUserId || user?.uuid || '';

  // Get user role information
  const authorMember = useMemo(() =>
    groupMembers.find(member => member.user.uuid === comment.user.uuid),
    [groupMembers, comment.user.uuid]
  );

  const isCurrentUser = comment.user.uuid === currentUserId;
  const hasReplies = comment.children && comment.children.length > 0;

  // Format time
  const commentTime = useMemo(() => {
    return formatRelativeTime(comment.createdAt);
  }, [comment.createdAt]);

  const validateUserProfile = useCallback(() => {
    return comment.user.deactivated;
  }, [comment.user.deactivated]);

  // Handle reply
  const handleReply = useCallback(() => {
    if (!allowReplies || !onReply) return;
    onReply(comment);
  }, [allowReplies, onReply, comment]);

  // Handle delete with confirmation
  const handleDelete = useCallback(async (commentToDelete?: GroupComment) => {
    if (!allowEditing) return;
    try {
      // If commentToDelete is provided, it's a reply being deleted
      // Otherwise, it's the parent comment being deleted
      const targetComment = commentToDelete || comment;
      await deleteCommentMutation.mutateAsync(targetComment.uuid);
      if (propOnDelete) {
        propOnDelete(targetComment);
      }
    } catch (error) {
      logger.error('Failed to delete comment:', error);
      <CobuAlert
        open={true}
        title={strings.alerts.titles.error}
        description={strings.alerts.error.deleteComment}
      />
      // Alert.alert(strings.alerts.titles.error, strings.alerts.error.deleteComment);
    }
  }, [/* allowEditing, deleteCommentMutation, comment, propOnDelete */]);

  // Handle block user
  const handleBlockUser = useCallback(async (userId: string) => {
    try {
      await blockUserMutation.mutateAsync({ currentUserId, userToBlock: userId });
      // Alert.alert(strings.alerts.titles.success, strings.alerts.success.blockUser);
    } catch (error) {
      logger.error('Failed to block user:', error);
      // Alert.alert(strings.alerts.titles.error, strings.alerts.error.blockUser);
    }
  }, [/* blockUserMutation, currentUserId, strings */]);

  const handleBlock = useCallback(async () => {
    await handleBlockUser(comment.user.uuid);
  }, [handleBlockUser, comment.user.uuid]);

  // Open report modal for a specific user
  const openReportModalForUser = useCallback((userId: string) => {
    setReportedUserId(userId);
    setIsReportModalVisible(true);
  }, []);

  // Handle report modal close
  const handleReportModalClose = useCallback(() => {
    setIsReportModalVisible(false);
  }, []);

  // Handle report success
  const handleReportSuccess = useCallback(() => {
    setReportSuccessAlertOpen(true);
  }, []);

  // Handle user profile navigation
  const handleUserProfile = useCallback(() => {
    if (!onUserProfile) return;
    if (validateUserProfile()) return;
    onUserProfile(comment.user.uuid);
  }, [onUserProfile, comment.user.uuid]);

  // Handle hide pinned comment
  const handleHidePinned = useCallback(() => {
    if (!onHidePinned) return;
    onHidePinned(comment.uuid);
  }, [onHidePinned, comment.uuid]);

  // Render user avatar
  const renderAvatar = useCallback(() => (
    <CobuView onPress={handleUserProfile}>
      {comment.user.profileImage !== URLS.DEFAULT_PROFILE_IMAGE ? (
        <img
          src={comment.user.profileImage}
          height={40}
          width={40}
          style={{ borderRadius: 20 }}
          onError={(e) => (e.currentTarget.src = '/assets/images/icon.png')}
          alt={Strings.get().ui.userAvatar.profileAlt}
        />
      ) : (
        <CobuSimpleAvatar
          user={{
            uuid: comment.user.uuid,
            displayName: comment.user.displayName || ''
          }}
          fontSize={!!comment.parent ? 14 : 17}
          size={40}
        />
      )}
    </CobuView>
  ), [comment.user, handleUserProfile]);

  // Render user info and timestamp
  const renderUserInfo = useCallback(() => (
    <CobuStack layout="horizontal" ai="center" gap="$2" f={1}>
      <CobuView onPress={handleUserProfile}>
        <CobuText
          variant="body"
          fow="$semibold"
        >
          {comment.user.displayName}
        </CobuText>
      </CobuView>

      <CobuText
        variant="caption"
      >
        {commentTime}
      </CobuText>
    </CobuStack>
  ), [comment.user, authorMember, commentTime, handleUserProfile]);

  // Render comment content
  const renderContent = useCallback(() => {
    if (comment.removed && comment.removedBy === 'USER') {
      return (
        <CobuText
          variant="body"
        >
          {strings.moderation.contentRemoved}
        </CobuText>
      );
    }

    if (!comment.contents) return null;
    
    return (
      <YStack gap="$2">
        {comment.type === 'WELCOME_MSG' && (
          <CobuText
            variant="body"
            color="$black"
          >
            {strings.welcomeMessage.greeting.replace('{firstName}', comment.user.firstName)}
          </CobuText>
        )}
        <ShowMoreText
          text={comment.contents}
          maxLength={190}
          maxLines={4}
          variant="body"
          color="$color"
          lineHeight={20}
        />
      </YStack>
    );
  }, [comment.contents, comment.type, comment.user.firstName, comment.removed, comment.removedBy]);

  // Render comment images
  const renderImages = useCallback(() => {
    if (comment.removed || !comment.images || comment.images.length === 0) {
      return null;
    }
    return (
      <CommentImageLayout
        images={comment.images}
        isParentComment={!comment.parent}
        isAuthor={isCurrentUser}
      />
    );
  }, [comment.removed, comment.images, comment.parent, isCurrentUser]);

  // Render embedded place card + share-type badge for a shared-place post
  const renderPlaceEmbed = useCallback(() => {
    // Render the embed whenever a place is attached — including auto-shares from
    // the add flow, which carry no placePostType. The type badge is not shown.
    if (comment.removed || !comment.place) return null;
    return <PlaceEmbedCard place={comment.place} />;
  }, [comment.removed, comment.place]);

  // Render reaction buttons and counts
  const renderReactions = useCallback(() => {
    if ((!allowReactions && !allowReplies) || comment.removed || comment.disableReplies) return null;

    return (
      <YStack gap="$2">
        {/* Reaction Summary - appears above the buttons */}
        {allowReactions && (
          <ReactionSummary
            comment={comment}
            currentUserId={currentUserId}
            onReactToComment={onReact ? async (commentId, reaction) => {
              onReact(commentId, reaction);
              return Promise.resolve();
            } : undefined}
            onShowReactionModal={onShowReactionModal}
          />
        )}

        {/* React and Reply buttons on the same line */}
        <XStack ai="center" jc="space-between" px="10%" btw={0.5} bbw={0.5} boc="$borderColor" py="$1">
          {allowReactions ? (
            <EnhancedReactionSelector
              comment={comment}
              currentUserId={currentUserId}
              isParent={true}
              onReactToComment={onReact ? async (commentId, reaction) => {
                onReact(commentId, reaction);
                return Promise.resolve();
              } : undefined}
              disabled={replyLoading}
            />
          ) : <YStack />}

          {allowReplies && (
            <CobuView onPress={handleReply} disabled={replyLoading}>
              <XStack ai="center" gap="$1">
                <ReplyIcon width={24} height={24} />
                <CobuText
                  variant="caption"
                >
                  {strings.buttons.reply}
                </CobuText>
              </XStack>
            </CobuView>
          )}
        </XStack>
      </YStack>
    );
  }, [
    allowReactions,
    allowReplies,
    comment,
    currentUserId,
    onReact,
    onShowReactionModal,
    handleReply,
    replyLoading,
    strings,
    theme
  ]);

  // Render action menu
  const renderActions = useCallback(() => {
    if (comment.removed && !isPinned) return null;

    return (
      <CommentActions
        comment={comment}
        isCurrentUser={isCurrentUser}
        isPinned={isPinned}
        allowEditing={allowEditing}
        allowReporting={allowReporting}
        onEdit={onEdit}
        onDelete={handleDelete}
        onReport={() => openReportModalForUser(comment.user.uuid)}
        onBlock={handleBlock}
        onFlag={onFlagComment ? () => onFlagComment(comment.uuid) : undefined}
        onHidePinned={handleHidePinned}
      />
    );
  }, [
    comment.removed,
    isPinned,
    comment,
    isCurrentUser,
    allowEditing,
    allowReporting,
    onEdit,
    handleDelete,
    openReportModalForUser,
    handleBlock,
    onFlagComment,
    handleHidePinned
  ]);

  // Render replies section
  const renderReplies = useCallback(() => {
    if (!hasReplies || comment.disableReplies) return null;

    return (
        <CommentReplies
        parentComment={comment}
        replies={comment.children}
        currentUserId={currentUserId}
        groupMembers={groupMembers}
        highlightReplyId={highlightReplyId}
        forceExpandReplies={forceExpandReplies}
        onReply={onReply}
        onReact={onReact}
        onEdit={onEdit}
        onDelete={(replyComment) => handleDelete(replyComment)}
        onReport={openReportModalForUser}
        onUserProfile={onUserProfile}
        allowReactions={allowReactions}
        allowReplies={allowReplies}
        allowEditing={allowEditing}
        allowReporting={allowReporting}
        onShowReactionModal={onShowReactionModal}
        onFlagComment={onFlagComment}
        onBlockUser={handleBlockUser}
      />
    );
  }, [
    hasReplies,
    comment,
    currentUserId,
    groupMembers,
    onReply,
    onReact,
    onEdit,
    handleDelete,
    onReport,
    onUserProfile,
    allowReactions,
    allowReplies,
    allowEditing,
    allowReporting,
    onShowReactionModal,
    highlightReplyId,
    forceExpandReplies
  ]);

  return (
    <YStack mx="$2" my="$2">
      {isReportModalVisible && (
        <ReportUserModal
          visible={isReportModalVisible}
          onClose={handleReportModalClose}
          reportedUserUuid={reportedUserId}
          reportedByUuid={currentUserId}
          onSuccess={handleReportSuccess}
        />
      )}
      <CobuAlert
        open={reportSuccessAlertOpen}
        onOpenChange={setReportSuccessAlertOpen}
        title={Strings.get().ui.reportUser.userReported}
        description={Strings.get().ui.reportUser.userReportedMessage}
        onAccept={() => {
          setReportSuccessAlertOpen(false);
        }}
      />
      <CobuCard
        padding="$2"
        bc={isPinned ? "$pinnedCommentGray" : "$white"}
        borderRadius="$3"
        borderWidth={isHighlighted ? 2 : 0}
        borderColor={isHighlighted ? "$cobuTeal" : undefined}
      >

        {/* Main comment content */}
        <XStack gap="$3">
          {/* Avatar */}
          {renderAvatar()}

          {/* Content area */}
          <YStack f={1} gap="$2">
            {/* User info header */}
            <XStack ai="center" jc="space-between">
              {renderUserInfo()}
              {renderActions()}
            </XStack>

            {/* Comment text */}
            {renderContent()}

            {/* Embedded place card, for shared-place posts */}
            {renderPlaceEmbed()}

            {/* Comment images */}
            {renderImages()}

            {/* Reactions and reply button */}
            {renderReactions()}
          </YStack>
        </XStack>

        {/* Replies section */}
        {renderReplies()}

        {/* Loading state for replies */}
        {replyLoading && (
          <XStack ai="center" gap="$2" mt="$3" ml="$10">
            {/* <MaterialIcons
              name="hourglass-empty"
              size={16}
              color={theme.cobuTeal.val}
            /> */}
            <CobuText
              variant="caption"
            >
              {strings.actions.replying}
            </CobuText>
          </XStack>
        )}
      </CobuCard>
    </YStack>
  );
});




CommentItem.displayName = 'CommentItem';



export default CommentItem; 